import React from "react";
import { describeConfiguration } from "../lib/orderSummary";
import { formatCHF } from "../lib/pricing";

// Mapping designId → fichier silhouette caducée
const CADUCEE_SRC = {
  "1": "/caducee/caducee-1.png",
  "2": "/caducee/caducee-2.png",
  "3": "/caducee/caducee-3.png",
  "4": "/caducee/caducee-4.svg",
  "5": "/caducee/caducee-7.png",
  "6": "/caducee/caducee-5.svg",
  "7": "/caducee/caducee-6.svg",
  "8": "/caducee/caducee-8.svg",
  "9": "/caducee/caducee-9.svg",
  "10": "/caducee/caducee-10.svg",
  "11": "/caducee/caducee-11.svg",
};
const CADUCEE_METAL_FILLS = {
  "gold-brillant": "linear-gradient(180deg, #FFF3C9 0%, #FFE08A 8%, #E0A938 24%, #8C5A0A 48%, #5B3700 52%, #8C5A0A 58%, #E0A938 78%, #FFD45A 94%, #FFF3C9 100%)",
  "gold-brosse":   "linear-gradient(180deg, #E9C575 0%, #C99D3D 30%, #8A6112 55%, #B98F2E 80%, #E0BB6A 100%)",
  "cuivre-poli":   "linear-gradient(180deg, #FFE0C2 0%, #F2A877 8%, #C57238 24%, #6F3A12 48%, #3F1F05 52%, #6F3A12 58%, #C57238 78%, #F0A270 94%, #FFE0C2 100%)",
  "cuivre-brosse": "linear-gradient(180deg, #DDA075 0%, #B47148 30%, #774524 55%, #A66B43 80%, #D89B73 100%)",
  "inox-poli":     "linear-gradient(180deg, #ffffff 0%, #f6f6f6 6%, #c8c8c8 22%, #6b6b6b 48%, #3b3b3b 52%, #6b6b6b 58%, #c8c8c8 78%, #f0f0f0 94%, #ffffff 100%)",
  "inox-brosse":   "linear-gradient(180deg, #e4e4e4 0%, #c2c2c2 30%, #8a8a8a 55%, #b0b0b0 80%, #d4d4d4 100%)",
};
const CADUCEE_LED_HEX = {
  warm3000: "#FFD18A", cool6500: "#EAF6FF",
  blue: "#00B6FF", green: "#22FF00", red: "#FF1F2D",
};

/**
 * Mini-preview du produit configuré, calé sur la matière + LED choisies.
 * Utilisé dans la sidebar de récapitulatif au checkout et dans la
 * confirmation de paiement.
 */
function CaduceePreviewTile({ cfg, size = 96 }) {
  const src = CADUCEE_SRC[cfg.designId] || CADUCEE_SRC["1"];
  const isRal = cfg.finishType === "ral";
  const bg = isRal ? (cfg.ralHex || "#888") : (CADUCEE_METAL_FILLS[cfg.finishId] || CADUCEE_METAL_FILLS["gold-brillant"]);
  const ledHex = CADUCEE_LED_HEX[cfg.ledId] || CADUCEE_LED_HEX.warm3000;
  const filter = cfg.ledOn !== false
    ? `drop-shadow(0 0 4px ${ledHex}) drop-shadow(0 0 10px ${ledHex}cc) drop-shadow(0 0 22px ${ledHex}88)`
    : "drop-shadow(0 2px 4px rgba(0,0,0,0.6))";
  return (
    <div className="shrink-0 flex items-center justify-center bg-black border border-white/10" style={{ width: size, height: size }}>
      <div style={{
        width: size * 0.78,
        height: size * 0.78,
        WebkitMaskImage: `url('${src}')`,
        maskImage: `url('${src}')`,
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        background: bg,
        filter,
      }} />
    </div>
  );
}

/** Vignette générique avec l'image produit + halo LED si applicable. */
function GenericTile({ item, size = 96 }) {
  const previewImg = item.configuration?.previewImage;
  if (previewImg) {
    return (
      <div className="shrink-0 bg-black border border-[#39FF14]/30 overflow-hidden" style={{ width: size, height: size }}>
        <img src={previewImg} alt="Aperçu configuré" className="w-full h-full object-contain" />
      </div>
    );
  }
  return (
    <div className="shrink-0 bg-black border border-white/10 overflow-hidden" style={{ width: size, height: size }}>
      <img src={item.image} alt="" className="w-full h-full object-cover" />
    </div>
  );
}

export default function OrderItemCard({ item, lang = "fr", compact = false }) {
  const slug = item.product_slug;
  const cfg = item.configuration || {};
  const name = (typeof item.product_name === "string"
    ? item.product_name
    : (item.product_name?.[lang] || item.product_name?.fr || slug)) || slug;
  const lines = describeConfiguration(slug, cfg, lang);
  const unit = Number(item.unit_price_chf) || 0;
  const qty = item.quantity || 1;
  const total = unit * qty;
  const tileSize = compact ? 72 : 96;

  return (
    <div className="flex gap-4 py-3" data-testid={`order-item-${slug}`}>
      {slug === "caducee" ? (
        <CaduceePreviewTile cfg={cfg} size={tileSize} />
      ) : (
        <GenericTile item={item} size={tileSize} />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-display font-bold text-white text-base leading-tight truncate">
            {name}
          </div>
          <div className="font-mono text-xs text-zinc-400 shrink-0">×{qty}</div>
        </div>
        {lines.length > 0 && (
          <ul className="mt-1.5 space-y-0.5">
            {lines.map((l, i) => (
              <li key={i} className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 truncate">
                <span className="text-[#39FF14] mr-1">·</span>{l}
              </li>
            ))}
          </ul>
        )}
        {total > 0 && (
          <div className="mt-2 flex items-baseline gap-3">
            {qty > 1 && (
              <span className="font-mono text-[10px] text-zinc-500">
                {formatCHF(unit)} / unité
              </span>
            )}
            <span className="font-display text-sm font-bold text-[#39FF14] tabular-nums">
              {formatCHF(total)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
